/**
 * CallList — paginated/filterable list of voice sessions.
 *
 * Routes: /app/calls. Each row → /app/calls/:id (CallDetail).
 * Header includes a "Test call" CTA for the WebRTC web-call (/app/calls/test).
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCallList, type CallListFilters } from "../hooks/useCallList";
import { Button, Card, Mono, Pill } from "../components/v3";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
  { value: "failed", label: "Failed" },
  { value: "dropped", label: "Dropped" },
] as const;

const LIMIT = 50;

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusToTone(s: string): "amber" | "ok" | "rose" | "warn" | "neutral" {
  return (
    {
      active: "amber",
      starting: "amber",
      ended: "neutral",
      failed: "rose",
      dropped: "warn",
    } as const
  )[s] ?? "neutral";
}

function outcomeTone(o: string | null): "ok" | "rose" | "warn" | "neutral" {
  if (o === "success") return "ok";
  if (o === "failure") return "rose";
  if (o === "incomplete") return "warn";
  return "neutral";
}

export function CallList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const filters: CallListFilters = {
    limit: LIMIT,
    offset: page * LIMIT,
    status: statusFilter || undefined,
  };

  const { sessions, total, isLoading, error } = useCallList(filters);
  const totalPages = Math.ceil(total / LIMIT);

  function handleFilterChange(s: string) {
    setStatusFilter(s);
    setPage(0);
  }

  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <Mono className="block mb-2">All calls</Mono>
          <h1 className="font-display text-3xl leading-none">
            {total > 0 ? (
              <>
                <span>{total.toLocaleString()}</span>{" "}
                <span className="text-t2">in the log</span>
              </>
            ) : (
              <span className="text-t2">No calls yet</span>
            )}
          </h1>
        </div>
        <Button as="a" href="/app/calls/test" variant="amber" size="sm">
          Test call
        </Button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUS_OPTIONS.map((opt) => {
          const active = statusFilter === opt.value;
          return (
            <button
              key={opt.value || "all"}
              onClick={() => handleFilterChange(opt.value)}
              className={[
                "px-3.5 py-1.5 rounded-pill border text-xs font-medium tracking-tight transition-colors",
                active
                  ? "bg-amber/15 text-amber border-amber/40"
                  : "border-line text-t2 hover:text-t1 hover:border-t4",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* List — wraps a horizontal scroll on narrow viewports so the table
          stays one row per call instead of compressing into illegibility. */}
      <Card padding="none" className="overflow-x-auto">
        <div className="min-w-[820px]">
        <div className="grid grid-cols-[110px_70px_1fr_70px_90px_110px_60px_40px] gap-3 px-5 py-3 border-b border-line-soft">
          <Mono>Started</Mono>
          <Mono>Direction</Mono>
          <Mono>Number</Mono>
          <Mono>Duration</Mono>
          <Mono>Outcome</Mono>
          <Mono>Stage</Mono>
          <Mono className="text-center">Anom.</Mono>
          <span />
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="skeleton h-12 mb-2" />
            <div className="skeleton h-12 mb-2" />
            <div className="skeleton h-12" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-rose">{error.message}</div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="font-display text-xl text-t2">No calls match</div>
            <div className="text-t3 text-sm mt-2">
              Try clearing filters, or place a{" "}
              <Link to="/app/calls/test" className="text-amber hover:underline">
                test call
              </Link>
              .
            </div>
          </div>
        ) : (
          sessions.map((s) => {
            const number =
              s.direction === "inbound" ? s.caller_number : s.callee_number;
            return (
              <button
                key={s.id}
                onClick={() => navigate(`/app/calls/${s.id}`)}
                className="grid grid-cols-[110px_70px_1fr_70px_90px_110px_60px_40px] gap-3 px-5 py-3 border-b border-line-soft last:border-b-0 hover:bg-white/[0.025] text-left items-center transition-colors w-full"
              >
                <span className="text-t3 text-xs font-mono">
                  {formatTime(s.started_at)}
                </span>
                <Pill tone={s.direction === "inbound" ? "plum" : "amber"}>
                  {s.direction === "inbound" ? "In" : "Out"}
                </Pill>
                <span className="font-mono text-[13px] text-t1 truncate">
                  {number ?? "—"}
                </span>
                <span className="text-t2 text-xs">
                  {formatDuration(s.duration_seconds)}
                </span>
                <Pill tone={outcomeTone(s.task_outcome)}>
                  {s.task_outcome ?? "—"}
                </Pill>
                <span className="text-t3 text-xs font-mono truncate">
                  {s.current_stage ?? "—"}
                </span>
                <span
                  className={`text-xs text-center font-mono ${
                    (s.anomaly_count ?? 0) > 0 ? "text-rose" : "text-t3"
                  }`}
                >
                  {s.anomaly_count ?? 0}
                </span>
                <span className="text-t3 text-xs">→</span>
                {/* Hidden: status pill at end of row in tighter spaces */}
                <span className="hidden">{statusToTone(s.status)}</span>
              </button>
            );
          })
        )}
        </div>
      </Card>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            ← Previous
          </Button>
          <Mono>
            Page {page + 1} of {totalPages}
          </Mono>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page + 1 >= totalPages}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}
